import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {notifier} from 'components';
import {TRANSLATION_KEYS} from 'locale';
import {pictureInPictureStore} from 'services';
import {logger} from 'utils';

const EVENTS = {
    ENTER_PICTURE_IN_PICTURE: 'enterpictureinpicture',
    LEAVE_PICTURE_IN_PICTURE: 'leavepictureinpicture',
    VISIBILITY_CHANGE: 'visibilitychange'
};

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.PICTURE_IN_PICTURE_OBSERVER;

export const PictureInPictureObserver: React.FC = () => {
    const {t} = useTranslation();
    const [videoElement, setVideoElement] = useState<HTMLVideoElement>(null);

    const handleEnter = useCallback(() => {
        pictureInPictureStore.mergeValue({active: true});
    }, []);

    const handleLeave = useCallback(() => {
        pictureInPictureStore.mergeValue({enabled: false, active: false});
    }, []);

    const subscribeVideoEvents = useCallback((video: HTMLElement) => {
        video.addEventListener(EVENTS.ENTER_PICTURE_IN_PICTURE, handleEnter);
        video.addEventListener(EVENTS.LEAVE_PICTURE_IN_PICTURE, handleLeave);
    }, [handleEnter, handleLeave]);

    const unsubscribeVideoEvents = useCallback(() => {
        videoElement?.removeEventListener(EVENTS.ENTER_PICTURE_IN_PICTURE, handleEnter);
        videoElement?.removeEventListener(EVENTS.LEAVE_PICTURE_IN_PICTURE, handleLeave);
    }, [handleEnter, handleLeave, videoElement]);

    const exitPictureInPicture = useCallback(async () => {
        if (document.pictureInPictureElement) {
            await document.exitPictureInPicture();
        }
        unsubscribeVideoEvents();
        pictureInPictureStore.mergeValue({active: false, enabled: false, container: null});
        setVideoElement(null);
    }, [unsubscribeVideoEvents]);

    const disable = useCallback(() => {
        pictureInPictureStore.mergeValue({enabled: false});
    }, []);

    const requestPictureInPicture = useCallback((video: HTMLVideoElement) => {
        const isVideoReady = video?.readyState > 0;

        if (!isVideoReady) {
            notifier.error({
                title: t(T_KEYS.ERROR.TITLE),
                details: t(T_KEYS.ERROR.NOT_READY)
            });
            exitPictureInPicture();
            return;
        }

        video.requestPictureInPicture().then(() => {
            pictureInPictureStore.mergeValue({active: true});
        }).catch((error) => {
            notifier.error({
                title: t(T_KEYS.ERROR.TITLE),
                details: error?.message
            });
            logger.error(error);
            exitPictureInPicture();
        });
    }, [t, exitPictureInPicture]);

    const initPictureInPicture = useCallback((container: string) => {
        const containerElement = document.querySelector(container);
        const video = containerElement?.querySelector('video');

        if (video) {
            setVideoElement(video);
            subscribeVideoEvents(video);
            requestPictureInPicture(video);
        } else {
            pictureInPictureStore.mergeValue({active: false, enabled: false});
        }
    }, [subscribeVideoEvents, requestPictureInPicture]);

    const handleVisibilityChange = useCallback(() => {
        if (document.visibilityState === 'visible' && pictureInPictureStore.getValue().active) {
            disable();
        }
    }, [disable]);

    // use subscribe instead useObservable to have is sync in order to prevent security issue
    // NotAllowedError: The request is not triggered by a user activation.
    useEffect(() => pictureInPictureStore.subscribe((newValue, prevValue) => {
        if (prevValue && newValue.enabled && !prevValue?.enabled) {
            initPictureInPicture(newValue.container);
        } else if (prevValue?.enabled && !newValue.enabled) {
            exitPictureInPicture();
        }
    }), [exitPictureInPicture, initPictureInPicture]);

    useEffect(() => {
        document.addEventListener(EVENTS.VISIBILITY_CHANGE, handleVisibilityChange);
        return () => {
            document.removeEventListener(EVENTS.VISIBILITY_CHANGE, handleVisibilityChange);
        };
    }, [handleVisibilityChange]);

    useEffect(() => {
        pictureInPictureStore.mergeValue({supported: document.pictureInPictureEnabled});
    }, []);

    useEffect(() => {
        if (videoElement) {
            const observer = new MutationObserver((mutationsList) => {
                for (const mutation of mutationsList) {
                    if (mutation.type === 'childList') {
                        const removedNodes = Array.from(mutation.removedNodes);
                        if (removedNodes.includes(videoElement)) {
                            exitPictureInPicture();
                            break;
                        }
                    }
                }
            });

            observer.observe(document.body, {childList: true, subtree: true});

            return () => {
                observer.disconnect();
            };
        }
    }, [videoElement, exitPictureInPicture]);

    return null;
};